import { forwardRef } from 'react';
import type { ThemeColor } from '@view-model/models/common/color';
import { ViewColor } from '../ViewStyle';
import type { ViewName } from '@view-model/domain/view';
import type { ViewId, ViewModelId } from 'balus-common';
import { ReadonlyViewTitle } from '../ViewTitleInput/ReadonlyViewTitle';
import { ViewTitleMenu } from '../ViewTitleMenu';
import { EditableViewTitle } from '../ViewTitleInput/EditableViewTitle';
import type { GetGuardProps } from '@model-framework/drag-file/useDragFileGuard';

type Props = {
  width: number;
  height: number;
  margin: number;
  menuContainerWidth: number;
  shouldHideMenuButtons: boolean;
  viewThemeColor: ThemeColor;
  name: ViewName;
  viewId: ViewId;
  viewModelId?: ViewModelId;
  isLocked?: boolean;
  isEditing?: boolean;
  readonly?: boolean;
  hasMeaningPanel?: boolean;
  onNameEndEdit?: () => void;
  onCopyView: () => void;
  onOpenPopupMenu: () => void;
  onAddMeaningPanel: () => void;
  onToggleMeaningPanel: () => void;
  onToggleShowNodeCreatedUser: () => void;
  getGuardProps: GetGuardProps;
};

export const BaseTitleBar = forwardRef<SVGRectElement, Props>(
  (
    {
      width,
      height,
      margin,
      menuContainerWidth,
      shouldHideMenuButtons,
      viewThemeColor,
      name,
      viewId,
      viewModelId,
      isLocked = false,
      isEditing = false,
      readonly = false,
      hasMeaningPanel = false,
      onNameEndEdit,
      onCopyView,
      onOpenPopupMenu,
      onAddMeaningPanel,
      onToggleMeaningPanel,
      onToggleShowNodeCreatedUser,
      getGuardProps,
    },
    ref
  ) => {
    const isReadonly = readonly || isLocked || !isEditing;

    return (
      <>
        <rect
          ref={ref}
          width={width}
          height={height}
          rx={height / 2}
          ry={height / 2}
          x={0}
          y={-height - margin * 2}
          stroke="none"
          cursor="move"
          fill={ViewColor.getTitleBarColor(viewThemeColor)}
          {...getGuardProps()}
        />
        <foreignObject
          width={width}
          height={height}
          transform={`translate(0, ${-height - margin * 2})`}
          className="pointer-events-none"
        >
          <div className={'flex items-center gap-8 px-16 text-[80px]'} style={{ height }}>
            {!shouldHideMenuButtons && <div className="shrink-0" style={{ width: menuContainerWidth }} />}
            {isReadonly ? (
              <ReadonlyViewTitle name={name} isLocked={isLocked} viewThemeColor={viewThemeColor} />
            ) : (
              <EditableViewTitle
                viewModelId={viewModelId!}
                viewId={viewId}
                isEditing={isEditing}
                name={name}
                onNameEndEdit={onNameEndEdit!}
                viewThemeColor={viewThemeColor}
              />
            )}
            {!shouldHideMenuButtons && (
              <ViewTitleMenu
                readonly={readonly}
                viewId={viewId}
                onCopyView={onCopyView}
                onOpenPopupMenu={onOpenPopupMenu}
                onAddMeaningPanel={onAddMeaningPanel}
                onToggleMeaningPanel={onToggleMeaningPanel}
                onToggleShowNodeCreatedUser={onToggleShowNodeCreatedUser}
                viewThemeColor={viewThemeColor}
                hasMeaningPanel={hasMeaningPanel}
              />
            )}
          </div>
        </foreignObject>
      </>
    );
  }
);

BaseTitleBar.displayName = 'BaseTitleBar';
